/**
 * The dashboard's alert sounds.
 *
 * Synthesised rather than loaded from a file: an asset is one more thing to
 * ship, cache-bust and get a 404 from, and WebAudio is always there. Both
 * sounds are deliberately short and quiet — they fire while somebody is in the
 * middle of doing something else.
 *
 * Two distinct shapes, because they mean different things and an admin should
 * not have to look up to find out which:
 *
 *  - **order** — rising, three notes, a little brighter. Money arrived.
 *  - **chat** — two notes, softer. Somebody is waiting for an answer.
 *
 * Browsers refuse to start audio until the page has been interacted with. There
 * is nothing to be done about that beyond failing quietly; every caller also
 * updates a badge, which needs no permission.
 */

const TUNES = {
  order: [
    { freq: 784, at: 0 },
    { freq: 1046.5, at: 0.11 },
    { freq: 1318.5, at: 0.22 },
  ],
  chat: [
    { freq: 880, at: 0 },
    { freq: 1174.7, at: 0.13 },
  ],
};

const PEAK_GAIN = { order: 0.22, chat: 0.18 };

/** @param {'order'|'chat'} kind */
export function playAdminChime(kind = 'chat') {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const notes = TUNES[kind] || TUNES.chat;
    const peak = PEAK_GAIN[kind] ?? 0.18;
    const now = ctx.currentTime;

    notes.forEach(({ freq, at }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      // Ramped rather than switched on: a square-edged start is a click.
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(peak, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.22);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.25);
    });

    const last = notes[notes.length - 1]?.at ?? 0;
    setTimeout(() => ctx.close().catch(() => {}), (last + 0.4) * 1000);
  } catch {
    // Sound is a courtesy, never a requirement.
  }
}

export default playAdminChime;
